import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { Option } from '@openx/types/options';
import { useCallback } from 'react';
import type { FilterCriteria, MultiOptionWithRangeConfigurableFilter } from '../../types';
import { RangeSelector } from './RangeSelector';

export type MultiOptionWithRangeFilterProps<Criteria extends FilterCriteria> = {
  options: Option[];
  appliedFilters?: FilterCriteria;
  onOptionsChange: (key: string, options: [string, string]) => void;
  config: MultiOptionWithRangeConfigurableFilter<Criteria>['config'];
};

type MultiOptionRangeProps<Criteria extends FilterCriteria> = {
  option: Option;
  optionConfig?: {
    endAdornment?: string;
    minValue?: number;
    maxValue?: number;
  };
  appliedFilters?: FilterCriteria;
  onOptionsChange: (key: string, options: [string, string]) => void;
  config: MultiOptionWithRangeConfigurableFilter<Criteria>['config'];
};

const MultiOptionRange = <Criteria extends FilterCriteria>({
  option,
  optionConfig,
  appliedFilters,
  onOptionsChange,
  config,
}: MultiOptionRangeProps<Criteria>) => {
  const handleOptionChange = useCallback(
    (value?: [string | undefined, string | undefined]) => onOptionsChange(option.id, value as [string, string]),
    [onOptionsChange, option.id]
  );

  return (
    <Box
      data-test={`range-option-${option.id}`}
      sx={{
        mb: 2,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          mb: 2,
        }}
      >
        {option.name}
      </Typography>

      <RangeSelector
        filterKey={option.id}
        selectedOptions={appliedFilters?.[option.id] as [string, string] | undefined}
        onOptionChange={handleOptionChange}
        config={{
          ...config,
          ...optionConfig,
          detailedOptionsConfig: {
            ...config.detailedOptionsConfig,
            minValue: optionConfig?.minValue,
            maxValue: optionConfig?.maxValue,
          },
        }}
      />
    </Box>
  );
};

export const MultiOptionWithRangeFilter = <Criteria extends FilterCriteria>({
  options,
  appliedFilters,
  onOptionsChange,
  config,
}: MultiOptionWithRangeFilterProps<Criteria>) => {
  const multiOptionsConfig = config?.detailedOptionsConfig?.multiOptionsConfig ?? [];

  return (
    <>
      {options.map((option, index) => (
        <MultiOptionRange
          key={option.id}
          option={option}
          optionConfig={multiOptionsConfig[index]}
          appliedFilters={appliedFilters}
          onOptionsChange={onOptionsChange}
          config={config}
        />
      ))}
    </>
  );
};
